/**
 * The outbox drain engine — ADR-0001 §4.2.
 *
 * This is the part of the system most likely to produce subtle bugs (ADR-0001 §7), so
 * the invariants it must hold are written down here rather than left implicit:
 *
 *  1. **Nothing is marked synced without a server confirmation naming that op.** Not on
 *     a 2xx, not on an empty response, not on a timeout that probably succeeded. The
 *     device is the only copy of a photo until the server says otherwise (§4.6 step 6).
 *  2. **An unanswered op stays pending.** At-least-once delivery: a response that omits
 *     an op tells us nothing, so we resend. Duplicate delivery is safe by construction
 *     because `opId` carries a unique index server-side.
 *  3. **FIFO per entity, and a terminal failure freezes its entity.** Applying an update
 *     over a create the server refused would attach evidence to a base that never
 *     existed. We stop that entity and surface it, rather than draining around it.
 *  4. **A transport throw changes nothing but attempt counts.** Partial application is
 *     the one outcome we cannot recover from, so the failure path touches no statuses.
 */

import type { Uuid } from './ids.js';
import type { Clock, OutboxRecord, OutboxStore, SyncTransport } from './ports.js';
import { systemClock } from './ports.js';
import type { OpError, OpResult, SyncOp } from './protocol.js';
import { CURRENT_SCHEMA_VERSION, PROTOCOL_VERSION, isTerminalErrorCode } from './protocol.js';

export interface BackoffPolicy {
  readonly baseMs: number;
  readonly maxMs: number;
  /** Multiplied into the delay to avoid a whole crew's phones retrying in lockstep. */
  readonly jitter: () => number;
}

export const defaultBackoff: BackoffPolicy = {
  baseMs: 1_000,
  maxMs: 5 * 60_000,
  jitter: () => 0.5 + Math.random() * 0.5,
};

export function backoffDelay(policy: BackoffPolicy, attemptCount: number): number {
  const exponential = policy.baseMs * 2 ** Math.max(0, attemptCount - 1);
  return Math.min(policy.maxMs, Math.round(exponential * policy.jitter()));
}

export interface DrainResult {
  /** Ops included in the request. */
  readonly attempted: number;
  readonly applied: number;
  /** Already-landed ops the server deduped. Successes, not failures. */
  readonly duplicates: number;
  readonly retrying: number;
  readonly failed: number;
  /** In the request but absent from the response. Left pending deliberately. */
  readonly unanswered: number;
  /** True when the transport itself threw; nothing was marked. */
  readonly transportError: boolean;
  readonly highestServerSeq: number | undefined;
}

const EMPTY_DRAIN: DrainResult = {
  attempted: 0,
  applied: 0,
  duplicates: 0,
  retrying: 0,
  failed: 0,
  unanswered: 0,
  transportError: false,
  highestServerSeq: undefined,
};

export interface OutboxDrainerOptions {
  readonly store: OutboxStore;
  readonly transport: SyncTransport;
  readonly clock?: Clock;
  readonly backoff?: BackoffPolicy;
  /** Ops per request. Kept modest: a small request finishes on a bad connection. */
  readonly batchSize?: number;
}

/** The error recorded when the transport throws, so the store keeps a real reason. */
const TRANSPORT_ERROR: OpError = {
  code: 'internal',
  message: 'transport error',
  retryable: true,
};

export class OutboxDrainer {
  readonly #store: OutboxStore;
  readonly #transport: SyncTransport;
  readonly #clock: Clock;
  readonly #backoff: BackoffPolicy;
  readonly #batchSize: number;

  /** Guards against two drains racing the same ops onto the wire. */
  #draining = false;

  constructor(options: OutboxDrainerOptions) {
    this.#store = options.store;
    this.#transport = options.transport;
    this.#clock = options.clock ?? systemClock;
    this.#backoff = options.backoff ?? defaultBackoff;
    this.#batchSize = options.batchSize ?? 25;
  }

  /**
   * Sends at most one batch.
   *
   * Callers drive this from a timer, from the "Sync now" button, and from the online
   * event (ADR-0001 §4.8). Returning a summary rather than throwing lets the UI report
   * "still 12 queued" honestly instead of showing an error and losing the count.
   */
  async drainOnce(): Promise<DrainResult> {
    if (this.#draining) return EMPTY_DRAIN;
    this.#draining = true;
    try {
      return await this.#drainBatch();
    } finally {
      this.#draining = false;
    }
  }

  /**
   * Drains until the queue is empty, nothing is sendable, or no forward progress is
   * made. The no-progress check is what stops a permanently-rejecting server from
   * turning this into a hot loop on someone's battery.
   */
  async drainAll(maxBatches = 50): Promise<DrainResult> {
    const total: Mutable<DrainResult> = { ...EMPTY_DRAIN };

    for (let i = 0; i < maxBatches; i += 1) {
      const batch = await this.drainOnce();
      accumulate(total, batch);

      if (batch.attempted === 0) break;
      if (batch.transportError) break;
      const progressed = batch.applied + batch.duplicates + batch.failed;
      if (progressed === 0) break;
    }

    return total;
  }

  async #drainBatch(): Promise<DrainResult> {
    const now = this.#clock.now();
    const records = await this.#store.listSendable(now, this.#batchSize);
    if (records.length === 0) return EMPTY_DRAIN;

    const ops = records.map((record) => record.op);
    const byOpId = new Map<Uuid, OutboxRecord>(records.map((r) => [r.op.opId, r]));

    let response;
    try {
      response = await this.#transport.pushOps({ protocolVersion: PROTOCOL_VERSION, ops });
    } catch {
      // Invariant 4. We do not know whether the server applied any of these, so we
      // change no statuses and let every op be resent. `opId` makes that safe.
      await this.#recordTransportFailure(records);
      return { ...EMPTY_DRAIN, attempted: ops.length, transportError: true };
    }

    return await this.#applyResults(response.results, byOpId, ops.length);
  }

  async #recordTransportFailure(records: readonly OutboxRecord[]): Promise<void> {
    const now = this.#clock.now();
    for (const record of records) {
      const attempt = record.attemptCount + 1;
      await this.#store.markRetry(
        record.op.opId,
        now + backoffDelay(this.#backoff, attempt),
        TRANSPORT_ERROR,
      );
    }
  }

  async #applyResults(
    results: readonly OpResult[],
    byOpId: ReadonlyMap<Uuid, OutboxRecord>,
    attempted: number,
  ): Promise<DrainResult> {
    const now = this.#clock.now();
    const answered = new Set<Uuid>();

    let applied = 0;
    let duplicates = 0;
    let retrying = 0;
    let failed = 0;
    let highestServerSeq: number | undefined;

    for (const result of results) {
      const record = byOpId.get(result.opId);
      // A result for an op we did not send is a server bug. Ignoring it is the safe
      // reading: acting on it could mark something synced that we never confirmed.
      if (record === undefined) continue;
      answered.add(result.opId);

      switch (result.status) {
        case 'applied':
        case 'duplicate': {
          await this.#store.markSynced(result.opId, result.seq);
          if (result.status === 'applied') applied += 1;
          else duplicates += 1;
          if (highestServerSeq === undefined || result.seq > highestServerSeq) {
            highestServerSeq = result.seq;
          }
          break;
        }
        case 'rejected': {
          const terminal =
            !result.error.retryable || isTerminalErrorCode(result.error.code);
          if (terminal) {
            await this.#store.markFailed(result.opId, result.error);
            // Invariant 3: freeze the entity so later ops do not apply over a gap.
            await this.#store.quarantineEntity(record.op.entityId, result.error);
            failed += 1;
          } else {
            const attempt = record.attemptCount + 1;
            await this.#store.markRetry(
              result.opId,
              now + backoffDelay(this.#backoff, attempt),
              result.error,
            );
            retrying += 1;
          }
          break;
        }
      }
    }

    // Invariant 2. Left pending on purpose — no status write, no backoff penalty.
    const unanswered = attempted - answered.size;

    return {
      attempted,
      applied,
      duplicates,
      retrying,
      failed,
      unanswered,
      transportError: false,
      highestServerSeq,
    };
  }
}

/**
 * Enqueues a mutation.
 *
 * Thin on purpose. The op is stamped once, here, and is immutable from this point on —
 * retries resend these exact bytes rather than re-deriving them from current state,
 * which is what makes a resend faithful to what the crew actually captured.
 */
export function buildOp(
  fields: Omit<SyncOp, 'schemaVersion'> & { schemaVersion?: number },
): SyncOp {
  const { schemaVersion, ...rest } = fields;
  return { ...rest, schemaVersion: schemaVersion ?? CURRENT_SCHEMA_VERSION };
}

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

function accumulate(total: Mutable<DrainResult>, batch: DrainResult): void {
  total.attempted += batch.attempted;
  total.applied += batch.applied;
  total.duplicates += batch.duplicates;
  total.retrying += batch.retrying;
  total.failed += batch.failed;
  total.unanswered += batch.unanswered;
  total.transportError = total.transportError || batch.transportError;
  if (
    batch.highestServerSeq !== undefined &&
    (total.highestServerSeq === undefined || batch.highestServerSeq > total.highestServerSeq)
  ) {
    total.highestServerSeq = batch.highestServerSeq;
  }
}
